import type {
  MarketplaceProductTrendPoint,
  MarketplaceTrendMarketPoint,
} from './marketplace-intelligence-dashboard';
import { isoDateDayDiff } from './time';

export type TrendChartDimensions = {
  width: number;
  height: number;
  paddingX: number;
  paddingY: number;
  // Optional per-side gutters for axis labels; each falls back to paddingX/paddingY,
  // so symmetric callers keep pixel-identical geometry.
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
};

export const TREND_CHART_DIMENSIONS: TrendChartDimensions = {
  width: 360,
  height: 150,
  paddingX: 18,
  paddingY: 14,
  paddingLeft: 48,
  paddingRight: 46,
  paddingBottom: 26,
};

export type TrendChartDot = {
  collectionDate: string;
  x: number;
  y: number;
};

export type TrendChartAxisTickX = {
  x: number;
  collectionDate: string;
  anchor: 'start' | 'middle' | 'end';
};

export type TrendChartAxisTickY = {
  y: number;
  valueRub: number;
};

export type TrendChartAxes = {
  x: TrendChartAxisTickX[];
  y: TrendChartAxisTickY[];
  band: { p25: TrendChartAxisTickY; p75: TrendChartAxisTickY } | null;
};

export type TrendChartGeometry = {
  productSegments: string[];
  gapConnectors: string[];
  marketLine: string;
  bandPolygon: string;
  productDots: TrendChartDot[];
  axes: TrendChartAxes;
};

function roundSvg(value: number): number {
  return Math.round(value * 100) / 100;
}

export function buildTrendChartGeometry(input: {
  points: MarketplaceProductTrendPoint[];
  marketSeries: MarketplaceTrendMarketPoint[];
  dimensions?: TrendChartDimensions;
}): TrendChartGeometry {
  const dimensions = input.dimensions ?? TREND_CHART_DIMENSIONS;
  const points = [...input.points].sort((left, right) => left.collectionDate.localeCompare(right.collectionDate));
  const marketSeries = [...input.marketSeries].sort((left, right) => left.collectionDate.localeCompare(right.collectionDate));

  const allDates = [
    ...points.map((point) => point.collectionDate),
    ...marketSeries.map((entry) => entry.collectionDate),
  ].sort();
  if (allDates.length === 0) {
    return {
      productSegments: [],
      gapConnectors: [],
      marketLine: '',
      bandPolygon: '',
      productDots: [],
      axes: { x: [], y: [], band: null },
    };
  }
  const firstDate = allDates[0];
  const lastDate = allDates[allDates.length - 1];
  const totalDays = isoDateDayDiff(firstDate, lastDate);

  const allValues = [
    ...points.flatMap((point) => [point.productPriceRub, point.marketMedianRub, point.marketP25Rub, point.marketP75Rub]),
    ...marketSeries.flatMap((entry) => [entry.marketMedianRub, entry.marketP25Rub, entry.marketP75Rub]),
  ];
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue || 1;

  const paddingLeft = dimensions.paddingLeft ?? dimensions.paddingX;
  const paddingRight = dimensions.paddingRight ?? dimensions.paddingX;
  const paddingTop = dimensions.paddingTop ?? dimensions.paddingY;
  const paddingBottom = dimensions.paddingBottom ?? dimensions.paddingY;
  const chartWidth = dimensions.width - paddingLeft - paddingRight;
  const chartHeight = dimensions.height - paddingTop - paddingBottom;
  const x = (collectionDate: string): number =>
    totalDays === 0
      ? roundSvg(paddingLeft + chartWidth / 2)
      : roundSvg(paddingLeft + (isoDateDayDiff(firstDate, collectionDate) / totalDays) * chartWidth);
  const y = (value: number): number =>
    roundSvg(paddingTop + (1 - (value - minValue) / valueRange) * chartHeight);
  const coordinate = (collectionDate: string, value: number): string => `${x(collectionDate)},${y(value)}`;

  const productSegments: string[] = [];
  const gapConnectors: string[] = [];
  let segment: string[] = [];
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    segment.push(coordinate(point.collectionDate, point.productPriceRub));
    const next = points[index + 1];
    if (next && isoDateDayDiff(point.collectionDate, next.collectionDate) > 1) {
      gapConnectors.push(
        `${coordinate(point.collectionDate, point.productPriceRub)} ${coordinate(next.collectionDate, next.productPriceRub)}`,
      );
      if (segment.length >= 2) {
        productSegments.push(segment.join(' '));
      }
      segment = [];
    }
  }
  if (segment.length >= 2) {
    productSegments.push(segment.join(' '));
  }

  const marketLine = marketSeries.length >= 2
    ? marketSeries.map((entry) => coordinate(entry.collectionDate, entry.marketMedianRub)).join(' ')
    : '';
  const bandPolygon = marketSeries.length >= 2
    ? [
        ...marketSeries.map((entry) => coordinate(entry.collectionDate, entry.marketP75Rub)),
        ...[...marketSeries].reverse().map((entry) => coordinate(entry.collectionDate, entry.marketP25Rub)),
      ].join(' ')
    : '';

  const productDots: TrendChartDot[] = points.map((point) => ({
    collectionDate: point.collectionDate,
    x: x(point.collectionDate),
    y: y(point.productPriceRub),
  }));

  const xTicks: TrendChartAxisTickX[] =
    firstDate === lastDate
      ? [{ x: x(firstDate), collectionDate: firstDate, anchor: 'middle' }]
      : [
          { x: x(firstDate), collectionDate: firstDate, anchor: 'start' },
          { x: x(lastDate), collectionDate: lastDate, anchor: 'end' },
        ];
  const yTicks: TrendChartAxisTickY[] =
    minValue === maxValue
      ? [{ y: y(maxValue), valueRub: maxValue }]
      : [
          { y: y(maxValue), valueRub: maxValue },
          { y: y(minValue), valueRub: minValue },
        ];
  const lastMarketEntry = marketSeries.length >= 2 ? marketSeries[marketSeries.length - 1] : null;
  const band = lastMarketEntry
    ? {
        p25: { y: y(lastMarketEntry.marketP25Rub), valueRub: lastMarketEntry.marketP25Rub },
        p75: { y: y(lastMarketEntry.marketP75Rub), valueRub: lastMarketEntry.marketP75Rub },
      }
    : null;

  return {
    productSegments,
    gapConnectors,
    marketLine,
    bandPolygon,
    productDots,
    axes: { x: xTicks, y: yTicks, band },
  };
}
